/**
 * The falling-sand core: movement of loose matter.
 *
 * Three deviations from the naive implementation, each load-bearing:
 *
 *  1. **Alternating column parity.** Rows go bottom-to-top; the direction of
 *     column iteration flips every tick. Naive left-to-right scanning gives
 *     sand a permanent leftward drift that players read as the world being
 *     tilted. Flipping costs nothing and removes the bias entirely.
 *
 *  2. **Displacement by density, not by type.** One comparison —
 *     `density(self) > density(other) * (1 + viscosity(other)*k)` — produces
 *     sand sinking in water, oil floating on water, gas bubbling up through
 *     liquid, and mercury sinking through everything. No type-pair table.
 *
 *  3. **Dispersion scan.** Liquids blocked below scan up to `dispersion` cells
 *     sideways looking for somewhere to fall, and settle at the furthest
 *     reachable cell otherwise. This is what makes water flatten and tar pile.
 *
 * Nothing here allocates.
 */

import { profiler } from '../core/profiler';
import type { Rng } from '../core/rng';
import { hash2 } from '../core/rng';
import { Category, Flag, M, VOID } from './materials';
import { CHUNK_MASK, CHUNK_SHIFT, CHUNK_SIZE, CellWorld, type Chunk } from './world';

export class CellSim {
  private world: CellWorld;
  private rng: Rng;

  /**
   * Resumable cursor, stored as a chunk *identity* rather than an index.
   *
   * `ordered` is rebuilt and re-sorted every tick from a changing active set,
   * so index k next tick is very often a different chunk — resuming by index
   * silently jumps to the wrong place and leaves the real remainder unvisited.
   */
  private cursorCx = 0;
  private cursorCy = 0;
  private cursorValid = false;
  private cursorRow = -1;

  /**
   * Deterministic work quota, in **work units** per tick.
   *
   * Deliberately NOT a wall-clock budget. docs/01-architecture.md §8 promises
   * the simulation is deterministic given (seed, tick, inputs), and scheduling
   * off `performance.now()` breaks that promise outright: the same seed on a
   * faster machine processes more cells per tick and diverges. A fixed work
   * quota gives the same throughput guarantee with reproducible results.
   *
   * Calibrated against the 2.5 ms budget in docs/01 §9. The previous value of
   * 60000 was ~4× too high: at a measured 150–241 ns per cell visited, a tick
   * that actually spent its whole quota cost 9–14 ms — the quota permitted a
   * frame-and-a-half of cell sim inside a 2.5 ms line item, so the budget was
   * met only by never being reached.
   *
   * A weighted unit measures 36–40 ns across the four stress scenes (Node 22,
   * darwin/arm64), which puts 24000 units at ~0.9 ms — inside budget with room
   * for a machine ~2.5× slower than the bench, and above the ~13000 units the
   * loudest fixture actually asks for, so nothing is throttled that fits.
   */
  maxCellsPerTick = 24000;

  /**
   * Work units charged for a cell that reaches the movement dispatch.
   *
   * A scanned cell costs 1 whether it is VOID or water, but a row of VOID and a
   * row of water differ by an order of magnitude in real time. Charging the
   * cells that actually move for what they cost is what makes one quota unit
   * mean roughly one fixed slice of a millisecond, which is the only property
   * that makes the quota a budget rather than a number.
   */
  private static readonly COST_ACTIVE = 4;
  /** Charged per cell stepped over by a liquid/gas dispersion scan. */
  private static readonly COST_SCAN = 1;

  /** Chunks sorted bottom-first for this tick. Reused, never reallocated. */
  private ordered: Chunk[] = [];

  /** Chunks that received a MOVED flag this tick, so `finish` can be bounded. */
  private movedChunks: Chunk[] = [];

  /** Work units spent this tick, beyond the flat per-scanned-cell charge. */
  private extraWork = 0;

  /** Telemetry. */
  movedCells = 0;
  /** Work units spent this tick, against `maxCellsPerTick`. Telemetry. */
  workThisTick = 0;

  constructor(world: CellWorld, rng: Rng) {
    this.world = world;
    this.rng = rng;
  }

  step(): void {
    profiler.begin('cellsim');
    this.movedCells = 0;
    this.extraWork = 0;

    const active = this.world.activeChunks;
    // Bottom-first: a cell must not fall into a chunk that has already been
    // processed this tick, or it moves twice.
    this.ordered.length = 0;
    for (let i = 0; i < active.length; i++) this.ordered.push(active[i]);
    // Module-level comparator, not an inline arrow: this runs every tick and
    // the closure was per-tick garbage in a system that claims to allocate none.
    this.ordered.sort(bottomFirst);

    const flip = (this.world.tick & 1) === 1;

    let checked = 0;

    // Resume at the chunk we parked on, by identity. If it is no longer active
    // (evicted, slept, or moved out of tier) we simply start from the top —
    // its dirty rect is preserved either way.
    let start = 0;
    if (this.cursorValid) {
      // Hand-rolled scan rather than `findIndex`: the predicate closed over
      // `this` and was allocated on every tick that resumed.
      let found = -1;
      for (let i = 0; i < this.ordered.length; i++) {
        const c = this.ordered[i];
        if (c.cx === this.cursorCx && c.cy === this.cursorCy) {
          found = i;
          break;
        }
      }
      if (found >= 0) start = found;
      else this.cursorRow = -1;
      this.cursorValid = false;
    }
    const resumeRow = this.cursorRow;
    this.cursorRow = -1;

    for (let ci = start; ci < this.ordered.length; ci++) {
      const c = this.ordered[ci];
      c.simPending = false;
      if (!c.hasDirty()) continue;

      const minX = c.dirtyMinX;
      const maxX = c.dirtyMaxX;
      const minY = c.dirtyMinY;
      const maxY = c.dirtyMaxY;
      const baseX = c.cx << CHUNK_SHIFT;
      const baseY = c.cy << CHUNK_SHIFT;

      // Resume mid-chunk only on the chunk we actually parked on.
      let y = ci === start && resumeRow >= 0 ? Math.min(resumeRow, maxY) : maxY;

      for (; y >= minY; y--) {
        const wy = baseY + y;
        if (flip) {
          for (let x = maxX; x >= minX; x--) this.stepCell(c, baseX + x, wy, x, y);
        } else {
          for (let x = minX; x <= maxX; x++) this.stepCell(c, baseX + x, wy, x, y);
        }

        checked += maxX - minX + 1;
        if (checked + this.extraWork >= this.maxCellsPerTick) {
          // Park the cursor and bail. Everything from here on is deferred,
          // not done — mark it pending so end-of-tick rotation leaves its
          // dirty rect intact. Rotating a chunk we never processed silently
          // discards the pending work, and the chunk then goes quiet and
          // falls asleep with matter frozen mid-air.
          this.cursorCx = c.cx;
          this.cursorCy = c.cy;
          this.cursorValid = true;
          this.cursorRow = y - 1;
          for (let k = ci; k < this.ordered.length; k++) {
            this.ordered[k].simPending = true;
          }
          this.workThisTick = checked + this.extraWork;
          this.finish();
          profiler.end('cellsim');
          return;
        }
      }
    }

    this.cursorValid = false;
    this.cursorRow = -1;
    this.workThisTick = checked + this.extraWork;
    this.finish();
    profiler.end('cellsim');
  }

  /**
   * Clear the MOVED flags this tick actually set.
   *
   * This used to walk every active chunk's whole dirty rect regardless of how
   * much of it the quota let `step` reach — including `simPending` chunks that
   * were never visited at all. Measured at 180,849 cells swept per tick against
   * a 60,000-cell quota (3.0×) and 1.23 ms, an unbudgeted second pass larger
   * than the budgeted first one.
   *
   * MOVED is only ever set by `markMoved`, which records the exact chunks and
   * a bounding box per chunk, so the clear is now proportional to the movement
   * rather than to the dirty area.
   */
  private finish(): void {
    const list = this.movedChunks;
    for (let i = 0; i < list.length; i++) {
      const c = list[i];
      for (let y = c.movedMinY; y <= c.movedMaxY; y++) {
        const row = y << CHUNK_SHIFT;
        for (let x = c.movedMinX; x <= c.movedMaxX; x++) {
          c.flags[row | x] &= ~Flag.MOVED;
        }
      }
      c.movedListed = false;
      c.movedMinX = CHUNK_SIZE;
      c.movedMinY = CHUNK_SIZE;
      c.movedMaxX = -1;
      c.movedMaxY = -1;
    }
    list.length = 0;
    profiler.setCount('moved', this.movedCells);
  }

  /**
   * `lx`/`ly` are chunk-local (for direct array access), `x`/`y` world coords
   * (for the neighbour queries that may cross chunk boundaries).
   */
  private stepCell(c: Chunk, x: number, y: number, lx: number, ly: number): void {
    const i = (ly << CHUNK_SHIFT) | lx;
    const flags = c.flags[i];
    if (flags & Flag.MOVED) return;

    const mat = c.mat[i];
    if (mat === VOID) return;
    if (M.mobile[mat] === 0) return;
    // Worldgen-placed structure and frozen matter don't settle.
    if (flags & (Flag.STATIC | Flag.FROZEN)) return;

    const cat = M.category[mat];

    // This cell is going to cost real time; charge the quota accordingly.
    this.extraWork += CellSim.COST_ACTIVE;

    // Transient matter ages out. Gases thin out and vanish; fire and lightning
    // are just short-lived cells rather than a special-cased effect system.
    const life = M.lifetime[mat];
    if (life > 0) {
      const age = c.aux[i];
      if (age >= life) {
        this.world.set(x, y, VOID);
        return;
      }
      c.aux[i] = age + 1;
    }

    if (cat === Category.GAS || cat === Category.PLASMA) {
      this.stepRising(x, y, mat);
    } else if (cat === Category.LIQUID || cat === Category.SLUDGE) {
      this.stepLiquid(x, y, mat);
    } else {
      this.stepLoose(x, y, mat);
    }
  }

  /**
   * Fall *through* a one-way surface rather than swapping with it.
   *
   * Only reachable once the caller's `probe` has already established that the
   * cell below *is* one-way, so the lookup is not repeated here. The old form
   * probed for one-wayness before the down-check on every loose and liquid cell
   * in the world — a `peekFor` + `M.oneWay` read that fails on essentially every
   * cell of every scene, measured at +0.212 ms/tick (+6.4%) in a world
   * containing no one-way cells at all.
   */
  private passThrough(x: number, y: number, density: number): boolean {
    const w = this.world;
    if (!w.canDisplace(x, y + 2, density)) return false;
    this.move(x, y, x, y + 2);
    return true;
  }

  /** Sand, gravel, ash, snow. Falls, then piles at an angle of repose. */
  private stepLoose(x: number, y: number, mat: number): void {
    const d = M.density[mat];
    const w = this.world;

    const below = w.probe(x, y + 1, d);
    if (below === CellWorld.PROBE_DISPLACE) {
      this.move(x, y, x, y + 1);
      return;
    }
    if (below === CellWorld.PROBE_ONE_WAY && this.passThrough(x, y, d)) return;

    // Diagonal slide. Randomised order so piles don't develop a lean.
    const dir = hash2(x, y, this.world.tick) & 1 ? 1 : -1;
    if (w.canDisplace(x + dir, y + 1, d) && !w.isSolid(x + dir, y)) {
      this.move(x, y, x + dir, y + 1);
      return;
    }
    if (w.canDisplace(x - dir, y + 1, d) && !w.isSolid(x - dir, y)) {
      this.move(x, y, x - dir, y + 1);
    }
  }

  /**
   * Liquids and sludges. Down, then diagonal, then a sideways scan bounded by
   * `dispersion`. The scan is what separates water (flattens, dispersion 5)
   * from tar (piles, dispersion 0).
   */
  private stepLiquid(x: number, y: number, mat: number): void {
    const d = M.density[mat];
    const w = this.world;
    const visc = M.viscosity[mat];

    // Viscous matter simply doesn't move on most ticks. Cheaper and more
    // stable than trying to model shear.
    if (visc > 0.3 && (hash2(x, y, this.world.tick) & 255) < visc * 220) return;

    const below = w.probe(x, y + 1, d);
    if (below === CellWorld.PROBE_DISPLACE) {
      this.move(x, y, x, y + 1);
      return;
    }
    if (below === CellWorld.PROBE_ONE_WAY && this.passThrough(x, y, d)) return;

    const dir = hash2(x, y, this.world.tick * 3) & 1 ? 1 : -1;
    if (w.canDisplace(x + dir, y + 1, d)) {
      this.move(x, y, x + dir, y + 1);
      return;
    }
    if (w.canDisplace(x - dir, y + 1, d)) {
      this.move(x, y, x - dir, y + 1);
      return;
    }

    const disp = M.dispersion[mat];
    if (disp === 0) return;

    // Scan outward for somewhere to fall; settle at the furthest reachable
    // cell if there isn't one. Checking the drop first is what makes water
    // actively seek level rather than just jitter sideways.
    let best = 0;
    for (let s = 1; s <= disp; s++) {
      this.extraWork += CellSim.COST_SCAN;
      const tx = x + dir * s;
      if (!w.canDisplace(tx, y, d)) break;
      if (w.canDisplace(tx, y + 1, d)) {
        this.move(x, y, tx, y + 1);
        return;
      }
      best = s;
    }
    if (best > 0) {
      // Slide sideways only when there is a body of matter behind us to level
      // out. Without this test an isolated droplet on flat ground slides
      // `dispersion` cells every single tick, forever, in a random direction —
      // perpetual motion that also keeps its chunk permanently awake.
      //
      // It used to be masked rather than absent: `finish` cleared MOVED across
      // each chunk's whole dirty rect, and a cell that dispersed past the edge
      // of that rect kept its MOVED flag into the next tick and was skipped.
      // Bounding the clear to the cells that actually moved (which is the
      // correct thing to do, and 3× cheaper) removed the accident, so the
      // condition has to be stated on purpose.
      //
      // The leading edge of a spreading sheet still advances: the cell behind
      // it is liquid. A lone droplet, and a droplet pinned against a wall
      // (solid behind), both stay put.
      const behind = w.peek(x - dir, y);
      if (behind !== VOID && M.solid[behind] === 0) {
        this.move(x, y, x + dir * best, y);
      }
    }
  }

  /** Gases and plasma. Same logic, inverted, plus lateral diffusion. */
  private stepRising(x: number, y: number, mat: number): void {
    const d = M.density[mat];
    const w = this.world;

    // Rise only if what's above is heavier — that's how gas bubbles up
    // through water but sits under a rock ceiling.
    if (this.canRiseInto(x, y - 1, d)) {
      this.move(x, y, x, y - 1);
      return;
    }

    const dir = hash2(x, y, this.world.tick * 7) & 1 ? 1 : -1;
    if (this.canRiseInto(x + dir, y - 1, d)) {
      this.move(x, y, x + dir, y - 1);
      return;
    }
    if (this.canRiseInto(x - dir, y - 1, d)) {
      this.move(x, y, x - dir, y - 1);
      return;
    }

    // Spread along the ceiling. Gases that can't rise must still fill the
    // volume, or methane pockets never reach the spark that lights them.
    const disp = M.dispersion[mat];
    for (let s = 1; s <= disp; s++) {
      this.extraWork += CellSim.COST_SCAN;
      const tx = x + dir * s;
      if (!this.canRiseInto(tx, y, d)) break;
      if (s === disp || this.canRiseInto(tx, y - 1, d)) {
        this.move(x, y, tx, y);
        return;
      }
    }
  }

  /** A rising cell may enter empty space or displace anything denser. */
  private canRiseInto(x: number, y: number, density: number): boolean {
    const m = this.world.peek(x, y);
    if (m === VOID) return true;
    if (M.solid[m] === 1) return false;
    return density < M.density[m];
  }

  /**
   * The single move primitive: swap, count it, and mark the destination MOVED
   * so it is not stepped twice in one tick.
   *
   * Both ends are recorded against the chunk's moved box, not just the
   * destination. `swap` exchanges *flags* along with matter, so a destination
   * that already carries MOVED hands that flag back to the source cell — and a
   * MOVED flag outside the box would never be cleared, silently freezing that
   * cell for the rest of the session.
   */
  private move(x1: number, y1: number, x2: number, y2: number): void {
    this.world.swap(x1, y1, x2, y2);
    this.movedCells++;
    this.markMoved(x2, y2);
    this.noteMoved(x1, y1);
  }

  private markMoved(x: number, y: number): void {
    const c = this.world.peekChunkAt(x, y);
    if (c === null) return;
    const lx = x & CHUNK_MASK;
    const ly = y & CHUNK_MASK;
    c.flags[(ly << CHUNK_SHIFT) | lx] |= Flag.MOVED;
    this.noteChunk(c, lx, ly);
  }

  /** Record a cell in the moved box without setting the flag. */
  private noteMoved(x: number, y: number): void {
    const c = this.world.peekChunkAt(x, y);
    if (c !== null) this.noteChunk(c, x & CHUNK_MASK, y & CHUNK_MASK);
  }

  private noteChunk(c: Chunk, lx: number, ly: number): void {
    if (!c.movedListed) {
      c.movedListed = true;
      this.movedChunks.push(c);
    }
    if (lx < c.movedMinX) c.movedMinX = lx;
    if (ly < c.movedMinY) c.movedMinY = ly;
    if (lx > c.movedMaxX) c.movedMaxX = lx;
    if (ly > c.movedMaxY) c.movedMaxY = ly;
  }
}

/** Bottom-first, then left-to-right. Module scope so `sort` allocates nothing. */
function bottomFirst(a: Chunk, b: Chunk): number {
  return b.cy - a.cy || a.cx - b.cx;
}
